import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useApi from '../../hooks/useApi';
import NodeContentTopSection from '../NodeContentTopSection';
import NodeContentHeaderSection from '../NodeContentHeaderSection';
import NodeContentSection from '../NodeContentSection';
import ChunkContentTeacherPanel from './nodes/ChunkContentTeacherPanel';
import TaskContentTeacherPanel from './nodes/TaskContentTeacherPanel';
import WrittenQuizContentTeacherPanel from './nodes/WrittenQuizContentTeacherPanel';
import QuizContentTeacherPanel from './nodes/QuizContentTeacherPanel';
import ChecklistContentTeacherPanel from './nodes/ChecklistContentTeacherPanel';
import EvaluationContentTeacherPanel from './nodes/EvaluationContentTeacherPanel';
import PublicBlogContentTeacherPanel from './nodes/PublicBlogContentTeacherPanel';
import PrivateBlogContentTeacherPanel from './nodes/PrivateBlogContentTeacherPanel';
import GlossaryContentTeacherPanel from './nodes/GlossaryContentTeacherPanel';
import CorrelationContentTeacherPanel from './nodes/CorrelationContentTeacherPanel';
import ArrowIcon from '../../icons/arrow-down-white.svg';
import ReportStateSelector from '../ReportStateSelector';
import {
  PanelWrapper,
  ContentWrapper,
  ContentToggle,
  ToggleText,
  ToggleIcon,
  TransitionWrapper,
  FooterPanel,
  NavigationPanel,
} from './styles';

const NodeContentTeacherPanel = ({
  nodeId,
  type: baseType,
  userToken,
  panel,
  options,
  student,
  flash,
  fileService,
}) => {
  const { uuid: studentId } = student;
  const { session_id, session_uuid, viewer } = options;
  const { getData, putData, loading } = useApi(userToken);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [report, setReport] = useState();
  const [reports, setReports] = useState();
  const [data, setData] = useState();
  const [showContent, setShowContent] = useState(false);
  const [contextId, setContextId] = useState('');
  const [updatingState, setUpdatingState] = useState(false);
  const [participations, setParticipations] = useState([]);

  const isArticle = ['chunk', 'video', 'link'].includes(baseType);

  useEffect(() => {
    const getNodeData = async () => {
      const nodeType = isArticle ? 'chunks' : 'behaviors';
      const response = await getData(`/api/v3/viewer/${nodeType}/${nodeId}/session/${session_uuid}/${studentId}`);
      if (response.success) {
        const body = response.body;
        setType(body.type || '');
        setTitle(body.name || '');
        setDescription((isArticle ? body.data.content : body.data.body) || '');
        setContextId(body.data.uuid);
        setReport(body.report);
        setReports(body.reports);
        setData({
          report: body.report,
          reports: body.reports,
          participations: body.participations,
          ...body.data,
        });
        setParticipations(body.participations);
      }
    };
    getNodeData();
  }, []);

  if (!data) {
    return null;
  }

  const { uuid: reportId } = report || {};

  const toggleComments = () => {
    document.dispatchEvent(new CustomEvent('toggleComments', { detail: { entityId: nodeId, entityType: 'node' } }));
  };

  const updateState = async (state, locked, callback = undefined) => {
    setUpdatingState(true);
    const payload = { report_uuid: reportId, state, locked };
    const route = baseType === 'chunk' ? 'chunk' : 'behaviors';
    const response = await putData(`/learn/${route}/${nodeId}/session/${session_id}/state`, payload);
    if (response.success) {
      setReport({
        ...report,
        state,
        locked,
      });
      document.dispatchEvent(new CustomEvent('progress.updated', { detail: response.body }));
      flash(i18n.__('flash_state_changed'), { ttl: 3000, type: 'status' });
    } else {
      console.error(response);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'), { ttl: 5000 });
      } else if (response.message === 'edit_conflict') {
        flash(i18n.__('error_edit_conflict_behavior'), { ttl: 5000 });
      } else if (response.message === 'payload_too_large') {
        flash(i18n.__('error_payload_too_large'), { ttl: 20000 });
      } else {
        flash(i18n.__('error_behavior_save_failed'));
      }
    }
    setUpdatingState(false);
    if (callback) {
      callback(!!response.success);
    }
  };

  const topSection = (
    <NodeContentTopSection
      nodeType={type}
      showVoiceOverTool={true}
      showTeacherPanelLink={true}
      showDyslexicFontTool={false}
      nodeId={nodeId}
      sessionId={session_id}
      panel={panel}
      userToken={userToken}
      viewer={viewer}
      nodeData={data}
      title={title}
    />
  );

  const headerSection = (
    <NodeContentHeaderSection
      title={title}
      toggleComments={toggleComments}
      showContent={showContent}
      setShowContent={setShowContent}
    />
  );

  const contentSection = (
    <ContentWrapper>
      <ContentToggle onClick={() => setShowContent(!showContent)}>
        <ToggleText>{showContent ? i18n.__('hide_assignment') : i18n.__('show_assignment')}</ToggleText>
        <ToggleIcon showContent={showContent} src={ArrowIcon} />
      </ContentToggle>
      {showContent && (
        <TransitionWrapper>
          <NodeContentSection
            description={description}
            type={baseType === 'chunk' ? baseType : type}
            contextId={contextId}
          />
        </TransitionWrapper>
      )}
    </ContentWrapper>
  );

  const viewSection = () => {
    const getViewComponent = () => {
      switch (type) {
        case 'chunk':
        case 'video':
        case 'link':
          return ChunkContentTeacherPanel;
        case 'task':
        case 'group':
          return TaskContentTeacherPanel;
        case 'evaluation':
          return EvaluationContentTeacherPanel;
        case 'checklist':
          return ChecklistContentTeacherPanel;
        case 'written_quiz':
          return WrittenQuizContentTeacherPanel;
        case 'quiz':
          return QuizContentTeacherPanel;
        case 'private_blog':
          return PrivateBlogContentTeacherPanel;
        case 'public_blog':
          return PublicBlogContentTeacherPanel;
        case 'legend':
          return GlossaryContentTeacherPanel;
        case 'correlation':
          return CorrelationContentTeacherPanel;
      }
    };

    const ViewComponent = getViewComponent();
    return (
      <ViewComponent
        type={type}
        student={student}
        report={report}
        reports={reports}
        participations={participations}
        data={data}
        fileService={fileService}
      />
    );
  };

  const showPanel = !loading || (loading && updatingState);

  return (
    showPanel && (
      <PanelWrapper>
        {topSection}
        {headerSection}
        {contentSection}
        {viewSection()}
        <FooterPanel>
          <NavigationPanel>
            <ReportStateSelector
              report={report}
              isTeacher={true}
              allowLock={true}
              allowStates={true}
              updateState={updateState}
            />
          </NavigationPanel>
        </FooterPanel>
      </PanelWrapper>
    )
  );
};

export default NodeContentTeacherPanel;

NodeContentTeacherPanel.propTypes = {
  nodeId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userToken: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  student: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
  fileService: PropTypes.object.isRequired,
};
